import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, Avatar, message } from 'antd';
import { UserAddOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GlobalTable from '@/components/GlobalTable';
import { useTable } from '@/hooks/useTable';
import { useDrawer } from '@/hooks/useDrawer';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { usePermission } from '@/hooks/usePermission';
import { ConfirmModal, PermissionWrapper } from '@/index';
import UserForm from './components/UserForm';
import RolePermissionCards from './components/RolePermissionCards';
import { usersAPI } from '@/services/api/users';
import { UserResponse, PaginationParams } from '@/types/api';
import type { TableColumn, TableAction } from '@/types/table';

// 使用API类型
type User = UserResponse;

// 角色配置
const roleConfig = {
  admin: { label: '管理员', color: 'red' },
  publisher: { label: '发布者', color: 'blue' },
  viewer: { label: '只读', color: 'cyan' },
};

// 状态配置
const statusConfig = {
  active: { label: '活跃', color: 'green' },
  disabled: { label: '禁用', color: 'default' },
};

const UserManagement: React.FC = () => {
  const { tableRef, loading, dataSource, setDataSource, pagination, setPagination } = useTable<User>();
  const { openDrawer, closeDrawer } = useDrawer();
  const { config, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { hasPermission } = usePermission();
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 获取用户列表
  const fetchUsers = async (params?: PaginationParams) => {
    try {
      const response = await usersAPI.getUsers({
        page: pagination.current,
        size: pagination.pageSize,
        search: searchKeyword,
        ...params,
      });
      
      console.log('API返回的数据:', response);
      console.log('用户列表数据:', (response as any).list);
      
      setDataSource((response as any).list);
      setPagination(prev => ({
        ...prev,
        total: response.total,
        current: response.page,
        pageSize: response.size,
      }));
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchUsers();
  }, []);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchUsers({
      page: 1,
      size: pagination.pageSize,
      search: value,
    });
  };

  // 处理添加用户
  const handleAddUser = () => {
    openDrawer({
      title: '添加用户',
      width: 600,
      content: (
        <UserForm
          onSuccess={() => {
            //message.success('用户添加成功');
            closeDrawer();
            fetchUsers();
          }}
        />
      ),
    });
  };

  // 处理编辑用户
  const handleEditUser = (record: User) => {
    openDrawer({
      title: '编辑用户',
      width: 600,
      content: (
        <UserForm
          user={record}
          onSuccess={() => {
            //message.success('用户更新成功');
            closeDrawer();
            fetchUsers();
          }}
        />
      ),
    });
  };

  // 处理删除用户
  const handleDeleteUser = (record: User) => {
    showConfirmModal('delete', {
      data: {
        name: record.username,
        version: record.email,
      },
      deleteConfig: {
        itemName: '用户',
        warningText: '该用户的所有权限将被移除',
      },
      onConfirm: async () => {
        try {
          await usersAPI.deleteUser(record.id);
          message.success('用户删除成功');
          fetchUsers();
        } catch (error) {
          console.error('删除用户失败:', error);
          message.error('删除用户失败');
        }
      },
    });
  };

  // 表格列配置
  const columns: TableColumn<User>[] = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      render: (value: string, ) => (
        <Space>
          <Avatar size="small"  icon={<UserOutlined />} />
          <span>{value}</span>
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (value: string, record: User) => {
        const config = roleConfig[value as keyof typeof roleConfig];
        return (
          <Tag color={config.color}>
            {record.role_name || config.label}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string, record: User) => {
        const config = statusConfig[value as keyof typeof statusConfig];
        return (
          <Tag color={config.color}>
            {record.status_name || config.label}
          </Tag>
        );
      },
    },
    {
      title: '最近登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 180,
      render: (value: string) => {
        if (!value) {
          return '从未登录';
        }
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
      },
    },
  ];

  // 操作列配置
  const actions: TableAction<User>[] = [
    {
      label: <EditOutlined />,
      key: 'edit',
      type: 'text',
      onClick: handleEditUser,
      // 需要用户管理权限
      hidden: () => !hasPermission('user:manage'),
    },
    {
      label: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      key: 'delete',
      type: 'text',
      danger: true,
      onClick: handleDeleteUser,
      // 需要用户管理权限
      hidden: () => !hasPermission('user:manage'),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* 用户列表 */}
      <GlobalTable
        ref={tableRef}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
            fetchUsers({
              page,
              size: pageSize,
              search: searchKeyword,
            });
          },
        }}
        header={{
          title: '用户管理',
          subtitle: '管理平台用户及其权限设置',
          extra: (
            <PermissionWrapper permission="user:manage">
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleAddUser}
              >
                添加用户
              </Button>
            </PermissionWrapper>
          ),
        }}
        toolbar={{
          
          showSearch: true,
          searchPlaceholder: '搜索用户...',
          onSearch: handleSearch,
        }}
        actions={actions}
        rowKey="id"
      />

      {/* 角色权限说明 */}
      <RolePermissionCards />

      {/* 全局确认框 */}
      <ConfirmModal 
        config={{
          ...config,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        }}
      />
    </div>
  );
};

export default UserManagement;
